import { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';

interface NavLinkDef {
  to: string;
  label: string;
}

interface AppDef {
  id: string;
  label: string;
  paths: string[];
  links: NavLinkDef[];
}

// Define apps and their nav links
const appConfig: AppDef[] = [
  {
    id: 'airbnb',
    label: '🏠 Airbnb Manager',
    paths: ['/', '/guests', '/conversations', '/rules', '/claims', '/reviews', '/log', '/payments', '/invoices'],
    links: [
      { to: '/', label: 'Dashboard' },
      { to: '/guests', label: 'Guests' },
      { to: '/conversations', label: 'Conversations' },
      { to: '/rules', label: 'Rules' },
      { to: '/claims', label: 'Claims' },
      { to: '/reviews', label: 'Reviews' },
      { to: '/log', label: 'Log' },
      { to: '/payments', label: 'Pay invoices' },
      { to: '/payments/history', label: 'Attempt history' },
      { to: '/invoices', label: 'Invoices' },
      { to: '/settings', label: 'Settings ⚙️' },
    ],
  },
  {
    id: 'walter',
    label: '🐦 Walter Monitor',
    paths: ['/walter'],
    links: [{ to: '/walter', label: 'Walter 🐦' }],
  },
  {
    id: 'pipeline',
    label: '⚙️ Pipeline',
    paths: ['/pipeline'],
    links: [{ to: '/pipeline', label: 'Pipeline ⚙️' }],
  },
  {
    id: 'flights',
    label: '✈️ Flights',
    paths: ['/flights'],
    links: [
      { to: '/flights', label: 'Flights ✈️' },
      { to: '/flights/config', label: 'Config' },
    ],
  },
  {
    id: 'kb',
    label: '🧠 KB Viewer',
    paths: ['/kb'],
    links: [{ to: '/kb', label: 'KB 🧠' }],
  },
  {
    id: 'videos',
    label: '🎬 Video Library',
    paths: ['/videos'],
    links: [{ to: '/videos', label: 'Videos 🎬' }],
  },
  {
    id: 'intel',
    label: '🔍 Intel Platform',
    paths: ['/intel', '/intel/influencers', '/intel/episodes', '/intel/predictions', '/intel/pipeline', '/intel/stats', '/intel/claims'],
    links: [
      { to: '/intel', label: 'Scoreboard' },
      { to: '/intel/influencers', label: 'KOLs' },
      { to: '/intel/episodes', label: 'Episodes' },
      { to: '/intel/predictions', label: 'Predictions' },
      { to: '/intel/pipeline', label: 'Pipeline' },
      { to: '/intel/stats', label: 'Stats' },
      { to: '/intel/claims', label: 'Claims' },
    ],
  },
  {
    id: 'painpoints',
    label: '🎯 Painpoint Research',
    paths: ['/painpoints'],
    links: [{ to: '/painpoints', label: 'Niches' }],
  },
  {
    id: 'pcmonitor',
    label: '🖥️ PC Monitor',
    paths: ['/pc-monitor'],
    links: [{ to: '/pc-monitor', label: 'Candidates 🖥️' }],
  },
];

const pageTitles: Record<string, string> = {
  '/': 'Airbnb Manager',
  '/guests': 'Guests',
  '/conversations': 'Conversations',
  '/rules': 'Rules',
  '/claims': 'Claims',
  '/reviews': 'Reviews',
  '/log': 'Log',
  '/walter': 'Walter Monitor 🐦',
  '/pipeline': 'Pipeline Mission Control ⚙️',
  '/flights': 'Flight Tracker ✈️',
  '/flights/config': 'Flight Tracker Config ✈️',
  '/kb': 'KB Viewer 🧠',
  '/videos': 'Video Library 🎬',
  '/payments': 'Invoice Payments 💳',
  '/payments/history': 'Invoice Payments 💳',
  '/invoices': 'Invoices 🧾',
  '/intel': 'Intel Platform',
  '/intel/influencers': 'Intel Platform',
  '/intel/episodes': 'Intel Platform',
  '/intel/predictions': 'Intel Platform',
  '/intel/pipeline': 'Intel Platform',
  '/intel/stats': 'Intel Platform',
  '/intel/claims': 'Intel Platform',
  '/painpoints': 'Painpoint Research 🎯',
  '/pc-monitor': 'PC Monitor 🖥️',
};

function getActiveApp(pathname: string): AppDef {
  return appConfig.find(app => app.paths.some(p => p === pathname || (p !== '/' && pathname.startsWith(p + '/')))) || appConfig[0];
}

function AppSwitcher({ activeApp }: { activeApp: AppDef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentTitle = pageTitles[location.pathname] || activeApp.label;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xl font-bold text-gray-900 hover:text-gray-600 transition-colors select-none"
      >
        {currentTitle}
        <svg
          className={`w-4 h-4 mt-0.5 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="px-3 py-2 text-xs text-gray-400 uppercase tracking-wide font-medium border-b border-gray-100">
            Switch to
          </div>
          {appConfig.map((app) => (
            <button
              key={app.id}
              onClick={() => { navigate(app.paths[0]); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${
                app.id === activeApp.id ? 'text-gray-400' : 'text-gray-700'
              }`}
            >
              {app.label}
              {app.id === activeApp.id && (
                <span className="text-xs text-gray-400">current</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const activeApp = getActiveApp(location.pathname);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <AppSwitcher activeApp={activeApp} />
            <div className="flex gap-1 overflow-x-auto shrink-0">
              {activeApp.links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/' || link.to === '/intel'}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
